/**
 * Pure helpers for JobFamilyPickerDialog (ER-11645, Organization > Job Families).
 *
 * Kept framework-free so the ladder-order / label rules can be unit tested without
 * mounting the SFC (mirrors the approach used by sibling Job Family helpers, e.g.
 * jobFamilyLevelsTableRow.js).
 */

/**
 * Levels come back from the API ordered by ordinal ascending (rules.md §3.2). Every
 * picker/table renders ladder order — highest ordinal first, level 1 at the bottom.
 * Returns a new array; never mutates the input.
 */
export function sortLevelsDescending(levels) {
  if (!Array.isArray(levels)) return [];
  return [...levels].sort((a, b) => (b?.ordinal ?? 0) - (a?.ordinal ?? 0));
}

/**
 * "ordinal · name", e.g. "3 · Senior Engineer". The ordinal prefix is mandatory —
 * Level names are not unique within a Family, so the ordinal is the only thing
 * distinguishing two levels sharing a name (rules.md §9). Never persist this string;
 * it must be recomputed live from the Level id every render.
 */
export function formatLevelLabel(level) {
  if (!level) return "";
  const ordinal = level.ordinal ?? "";
  const name = level.name ?? "";
  return `${ordinal} · ${name}`;
}

/**
 * "Family › ordinal · Level", the full breadcrumb path used both for result rows and
 * the committed-selection chip.
 */
export function formatPathLabel(family, level) {
  if (!family) return "";
  const familyName = family.jobFamilyName ?? "";
  if (!level) return familyName;
  return `${familyName} › ${formatLevelLabel(level)}`;
}

/**
 * Map a raw Job Family list row into the shape the picker components expect:
 * `{ jobFamilyId, jobFamilyName, code, status, levelCount, levels }`. The API code
 * carries the shared `#` prefix (`#120`); the picker renders `#{{ code }}` itself,
 * so hand it the bare number. Levels are not nested in the family list payload,
 * so callers fetch them per family and pass them in as `family.levels`.
 *
 * ER-11950: added status and levelCount for the redesigned picker's status badges
 * and level count display.
 */
export function mapApiJobFamilyToPickerResult(family) {
  return {
    jobFamilyId: family.id,
    jobFamilyName: family.name,
    // The API code carries the shared "#" prefix (`#120`); the picker renders
    // `#{{ code }}` itself, so hand it the bare number.
    code: String(family.code || "").replace(/^#/, ""),
    status: family.status,
    levelCount: family.level_count ?? 0,
    levels: Array.isArray(family.levels) ? family.levels : [],
  };
}

/**
 * Live look-up of a Family by id from the caller-supplied `results` prop — never
 * cache the resolved object, always resolve from the current `results` on each use.
 */
export function findFamilyById(results, jobFamilyId) {
  if (!Array.isArray(results) || jobFamilyId == null) return null;
  return results.find((family) => family?.jobFamilyId === jobFamilyId) ?? null;
}

/**
 * Live look-up of a Level within a Family by id — see findFamilyById.
 */
export function findLevelById(family, levelId) {
  if (!family || !Array.isArray(family.levels) || levelId == null) return null;
  return family.levels.find((level) => level?.id === levelId) ?? null;
}
